import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { accountApi } from "../services/accountApi";
import { setApiAuthState } from "../services/apiClient";

/**
 * Which apiary am I working in? (#310)
 *
 * Somebody who helps at another apiary has two contexts, and the app used
 * to pick for them -- badly. Reported from dev: a keeper signed in to
 * their own new account, added a site and a hive, and both landed on the
 * account they help with. Their own apiary could not be given a single
 * site.
 *
 * The case that makes it obvious is a teaching apiary: students work the
 * owner's hives for a season and then set up their own. Under the old
 * inference, becoming a helper meant losing the ability to own anything.
 *
 * So the choice is explicit, it defaults to YOUR OWN account, and it is
 * visible whenever there is more than one thing it could be.
 *
 * Kept in sessionStorage rather than localStorage: "whose hives am I
 * touching" should not quietly persist across days and browser restarts.
 * Coming back tomorrow and finding yourself still pointed at somebody
 * else's apiary is exactly the surprise this exists to remove.
 */
const KEY = "beekeeper.acting_account";
const AccountContext = createContext(null);

export function AccountProvider({ children }) {
  const [accounts, setAccounts] = useState([]);      // accounts I can help with
  const [actingAccountId, setActing] = useState(() => {
    try {
      const v = sessionStorage.getItem(KEY);
      return v ? Number(v) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let cancelled = false;
    accountApi
      .listShares()
      .then((r) => {
        if (cancelled) return;
        // Approved shares addressed to me: the accounts I may act in.
        setAccounts((r.received || []).filter((sh) => sh.status === "Approved"));
      })
      // Best-effort, like every other secondary call: not knowing which
      // accounts exist must not stop the app opening in your own.
      .catch(() => !cancelled && setAccounts([]));
    return () => { cancelled = true; };
  }, []);

  // Push into the api client so every request carries it.
  useEffect(() => {
    setApiAuthState({ actingAccountId });
    try {
      if (actingAccountId) sessionStorage.setItem(KEY, String(actingAccountId));
      else sessionStorage.removeItem(KEY);
    } catch {
      /* a blocked sessionStorage must not break the app */
    }
  }, [actingAccountId]);

  const switchTo = useCallback((id) => {
    setActing(id || null);
    // Everything on screen belongs to the old account. Re-fetching page by
    // page would show a half-swapped app, which is worse than a reload.
    window.location.reload();
  }, []);

  const value = useMemo(() => ({
    actingAccountId,
    accounts,
    switchTo,
    // Only worth showing a control when there is a choice to make.
    hasChoice: accounts.length > 0,
    currentLabel: actingAccountId
      ? (accounts.find((a) => a.primary_user_id === actingAccountId)?.primary_email
         || "another apiary")
      : "My apiary",
  }), [actingAccountId, accounts, switchTo]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount() {
  return useContext(AccountContext) || {
    actingAccountId: null, accounts: [], hasChoice: false,
    currentLabel: "My apiary", switchTo: () => {},
  };
}
